import { ApiQueryName } from "@/models/api"
import { Meter } from "@/models/meter"
import type { Organization } from "@/models/organization"
import { useRenameQueryResult } from "@/services/utils"
import { useQueryKey } from "@/services/utils/useQueryKey"
import { HttpError, toHttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"
import type { RecordProxy } from "spraypaint/lib-esm/proxies"

export const isUnavailableMeterError = (error: unknown): boolean =>
  error instanceof HttpError && (error.status === 403 || error.status === 404)

interface GetMeterOptions {
  meterId: string
  organizationId: Organization["id"]
}

const getMeter = async ({
  meterId,
  organizationId,
}: GetMeterOptions): Promise<Meter> => {
  try {
    const response: RecordProxy<Meter> = await Meter.includes([
      "meter_source_type",
      "site",
    ])
      .where({ organization_id: organizationId })
      .selectExtra(["emission_factor_display"])
      .find(meterId)

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseMeterOptions {
  meterId: string | null
  organizationId: Organization["id"]
  queryOptions?: Omit<UseQueryOptions<Meter, HttpError>, "queryFn" | "queryKey">
}

export const useMeter = ({
  meterId,
  organizationId,
  queryOptions = {},
}: UseMeterOptions) => {
  const queryKey = useQueryKey(ApiQueryName.Meter, "getOne", meterId, {
    organizationId,
  })
  const {
    enabled,
    retry = (failureCount, error) => {
      if (isUnavailableMeterError(error)) {
        return false
      }

      return failureCount < 3
    },
    throwOnError = resolveQueryThrowOnError(
      (error) => !isUnavailableMeterError(error)
    ),
    ...restQueryOptions
  } = queryOptions

  const queryResult = useQuery<Meter, HttpError>({
    ...restQueryOptions,
    enabled: Boolean(organizationId) && meterId !== null && (enabled ?? true),
    queryFn: async () => {
      if (meterId === null) {
        throw new Error("meterId is required to fetch meter details")
      }

      return await getMeter({ meterId, organizationId })
    },
    queryKey,
    retry,
    throwOnError,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Meter)
}
